import React from "react";
import Link from "next/link";
import styles from "./styles/button.module.css";

type ButtonVariant =
  | "primary"
  | "success"
  | "warning"
  | "danger"
  | "secondary";

type ButtonAppearance = "filled" | "outline" | "dotted";
type ButtonSize = "sm" | "md" | "lg";

/* ======================
   BASE (shared props)
   ====================== */
interface BaseProps extends React.HTMLAttributes<HTMLElement> {
  variant?: ButtonVariant;
  appearance?: ButtonAppearance;
  size?: ButtonSize;
  fullWidth?: boolean;
  title: string;
  className?: string;
}

/* ======================
   BUTTON VARIANT
   ====================== */
type ButtonAsButton = BaseProps &
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    href?: never;
  };

/* ======================
   LINK VARIANT
   ====================== */
type ButtonAsLink = BaseProps & {
  href?: string;
  replace?: boolean;
  scroll?: boolean;
  prefetch?: boolean;
};

type ButtonProps = ButtonAsButton | ButtonAsLink;

export default function Button({
  variant = "primary",
  appearance = "filled",
  size = "md",
  fullWidth = false,
  className = "",
  title,
  href,
  ...props
}: ButtonProps) {
  const classes = [
    styles.button,
    styles[variant],
    styles[appearance],
    styles[size],
    fullWidth && styles.fullWidth,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  /* LINK */
  if (href) {
    return (
      <Link href={href} {...(props as ButtonAsLink)} className={classes}>
        {title}
      </Link>
    );
  }

  /* BUTTON */
  return (
    <button className={classes} {...(props as ButtonAsButton)}>
      {title}
    </button>
  );
}
